/**
 * Estrutura das telas: pilha de painéis e grades responsivas.
 *
 * Toda tela é uma coluna no celular e se abre em colunas conforme a largura. As grades
 * ficam aqui, com as classes escritas por inteiro, para que as nove telas respondam à
 * largura do mesmo jeito — e porque o Tailwind só gera classe que encontra escrita.
 */

import type { ReactNode } from "react";
import { motion, type Variants } from "motion/react";

/** Contêiner da tela: dispara a entrada dos painéis em cascata. */
export const PAGE_VARIANTS: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
};

/** Entradas da pilha de painéis. */
interface PageProps {
  readonly children: ReactNode;
}

/** Pilha vertical de painéis, com entrada em cascata. */
export function Page({ children }: PageProps): React.ReactElement {
  return (
    <motion.div className="flex flex-col gap-5" initial="hidden" animate="visible" variants={PAGE_VARIANTS}>
      {children}
    </motion.div>
  );
}

/** Quantidade de colunas nas telas largas. */
type GridColumns = 2 | 3 | 4;

/** Classes de cada grade: uma coluna no celular, abrindo conforme a largura. */
const GRID_CLASS: Readonly<Record<GridColumns, string>> = {
  2: "grid gap-5 lg:grid-cols-2",
  3: "grid gap-5 md:grid-cols-2 xl:grid-cols-3",
  4: "grid gap-5 sm:grid-cols-2 xl:grid-cols-4",
};

/** Entradas da grade de painéis. */
interface PanelGridProps {
  readonly children: ReactNode;
  readonly columns?: GridColumns;
}

/** Linha de painéis lado a lado, que empilha em telas estreitas. */
export function PanelGrid({ children, columns = 2 }: PanelGridProps): React.ReactElement {
  return <div className={GRID_CLASS[columns]}>{children}</div>;
}
